/**
 * Consensus Guide Modal
 * Fullscreen modal explaining how Smart Prompt's Consensus Analysis works
 * Based on docs/CONSENSUS-user-guide.md
 */

import React from 'react';
import { createPortal } from 'react-dom';
import { InteractiveClaimExample } from './InteractiveClaimExample';

interface ConsensusGuideModalProps {
  isOpen: boolean;
  onClose: () => void;
}

export const ConsensusGuideModal: React.FC<ConsensusGuideModalProps> = ({ isOpen, onClose }) => {
  if (!isOpen) return null;

  const modalTopPadding = 'calc(var(--main-nav-height, 4.5rem) + 1.5rem)';

  return createPortal(
    <div
      className="fixed inset-0 bg-black/80 z-50 flex items-start justify-center overflow-y-auto"
      onClick={onClose}
    >
      <div className="w-full min-h-screen px-4 pb-8" style={{ paddingTop: modalTopPadding }}>
        <div
          className="w-full max-w-4xl mx-auto bg-slate-900 rounded-xl shadow-2xl border border-slate-700"
          onClick={(e) => e.stopPropagation()}
        >
          {/* Header */}
          <div className="sticky top-0 bg-slate-900 border-b border-slate-700 p-6 flex items-center justify-between rounded-t-xl z-10">
            <h2 className="text-2xl font-bold text-white flex items-center gap-2">
              <span>✨</span>
              <span>How Consensus Analysis Works</span>
            </h2>
            <button
              onClick={onClose}
              className="text-slate-400 hover:text-white transition-colors text-2xl w-8 h-8 flex items-center justify-center rounded-lg hover:bg-slate-800"
              aria-label="Close modal"
            >
              ✕
            </button>
          </div>

          {/* Content */}
          <div className="p-6 space-y-8">
            {/* Introduction */}
            <div className="bg-slate-800/50 rounded-lg p-4 border-l-4 border-cyan-500">
              <p className="text-slate-300 text-sm leading-relaxed">
                A visual overview of how we analyze agreement across AI models in Smart Prompt.
                Keep this as your reference when exploring consensus results.
              </p>
            </div>

            {/* What is Consensus Analysis */}
            <section>
              <h3 className="text-xl font-bold text-white mb-4 flex items-center gap-2">
                <span className="text-cyan-400">🔷</span>
                What is Consensus Analysis?
              </h3>
              <div className="text-slate-300 text-sm leading-relaxed space-y-3">
                <p>
                  Consensus Analysis extracts the <strong>key claims</strong> from each AI model's response
                  and compares them to show you where models <strong>agree</strong>, <strong>partially agree</strong>,
                  or <strong>provide unique insights</strong>.
                </p>
                <p>Instead of reading through multiple full responses, you can quickly see:</p>
                <ul className="list-disc list-inside space-y-1 ml-4">
                  <li>Which facts all models agree on (high confidence)</li>
                  <li>Where models differ (needs your judgment)</li>
                  <li>Unique points only one model mentioned (potential value-add or outlier)</li>
                </ul>
              </div>
            </section>

            {/* How Claims Are Extracted */}
            <section>
              <h3 className="text-xl font-bold text-white mb-4 flex items-center gap-2">
                <span className="text-cyan-400">🧠</span>
                How Claims Are Extracted
              </h3>
              <div className="text-slate-300 text-sm leading-relaxed space-y-3">
                <p>
                  When you run Consensus Analysis, we use AI to extract only the <strong>substantive content</strong> from each response.
                </p>

                <div className="bg-slate-800/30 rounded-lg border border-slate-700 overflow-hidden">
                  <table className="w-full text-sm">
                    <thead className="bg-slate-800/50">
                      <tr>
                        <th className="px-4 py-3 text-left text-xs font-semibold text-slate-400 uppercase">What We Extract</th>
                        <th className="px-4 py-3 text-left text-xs font-semibold text-slate-400 uppercase">What We Filter Out</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-700">
                      <tr>
                        <td className="px-4 py-3">✅ Facts and statistics</td>
                        <td className="px-4 py-3">❌ "Let me explain..." intros</td>
                      </tr>
                      <tr>
                        <td className="px-4 py-3">✅ Opinions and assessments</td>
                        <td className="px-4 py-3">❌ "In conclusion..." wrap-ups</td>
                      </tr>
                      <tr>
                        <td className="px-4 py-3">✅ Recommendations and advice</td>
                        <td className="px-4 py-3">❌ "Here are some thoughts..." transitions</td>
                      </tr>
                      <tr>
                        <td className="px-4 py-3">✅ Warnings and caveats</td>
                        <td className="px-4 py-3">❌ Generic filler content</td>
                      </tr>
                    </tbody>
                  </table>
                </div>

                <div className="bg-cyan-900/20 border border-cyan-500/30 rounded-lg p-4">
                  <p className="text-sm text-cyan-200">
                    <strong>Why this matters:</strong> You see only the meaningful content that's worth comparing—not boilerplate that every model includes.
                  </p>
                </div>
              </div>
            </section>

            {/* How We Compare Claims */}
            <section>
              <h3 className="text-xl font-bold text-white mb-4 flex items-center gap-2">
                <span className="text-cyan-400">🔍</span>
                How We Compare Claims
              </h3>
              <div className="text-slate-300 text-sm leading-relaxed space-y-3">
                <p>
                  Once claims are extracted, we compare them using <strong>semantic similarity</strong>—meaning
                  we match claims by their <strong>meaning</strong>, not just their exact words.
                </p>

                <div className="bg-slate-800/30 rounded-lg border border-slate-700/50 p-4">
                  <p className="text-sm font-semibold text-white mb-2">Example:</p>
                  <ul className="space-y-1 text-sm">
                    <li>Model A says: <em>"Fresenius holds 40% market share"</em></li>
                    <li>Model B says: <em>"Fresenius controls approximately 40% of the market"</em></li>
                    <li><strong className="text-cyan-400">Result:</strong> These are matched as the same claim (96% similarity)</li>
                  </ul>
                </div>

                <p className="text-sm">
                  This ensures you see true agreement even when models phrase things differently.
                </p>
              </div>
            </section>

            {/* The Reference Claim */}
            <section>
              <h3 className="text-xl font-bold text-white mb-4 flex items-center gap-2">
                <span className="text-cyan-400">📌</span>
                The Reference Claim
              </h3>
              <div className="text-slate-300 text-sm leading-relaxed space-y-3">
                <p>
                  Each claim card displays a <strong>Reference Claim</strong> at the top—this is the primary statement shown to represent the group.
                </p>

                <div className="bg-slate-800/30 rounded-lg border border-slate-700/50 p-4">
                  <p className="text-sm font-semibold text-white mb-2">How it's selected:</p>
                  <ul className="list-disc list-inside space-y-1 ml-4">
                    <li>The Reference Claim has the <strong>highest similarity score</strong> to all other claims in the group</li>
                    <li>It represents the "most central" or "most representative" phrasing of the claim</li>
                    <li>Think of it as the consensus wording that best captures what multiple models said</li>
                  </ul>
                </div>

                <p className="text-sm">
                  Click <strong>"Show variations"</strong> to see how each model phrased it differently.
                </p>
              </div>
            </section>

            {/* The 4 Agreement Categories */}
            <section>
              <h3 className="text-xl font-bold text-white mb-4 flex items-center gap-2">
                <span className="text-cyan-400">🎯</span>
                The 4 Agreement Categories
              </h3>

              <div className="bg-slate-800/30 rounded-lg border border-slate-700 overflow-hidden">
                <table className="w-full text-sm">
                  <thead className="bg-slate-800/50">
                    <tr>
                      <th className="px-4 py-3 text-left text-xs font-semibold text-slate-400 uppercase">Icon</th>
                      <th className="px-4 py-3 text-left text-xs font-semibold text-slate-400 uppercase">Category</th>
                      <th className="px-4 py-3 text-left text-xs font-semibold text-slate-400 uppercase">What It Means</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-700">
                    <tr className="hover:bg-slate-800/30">
                      <td className="px-4 py-3 text-2xl">🟢</td>
                      <td className="px-4 py-3 font-semibold text-white">Universal Agreement</td>
                      <td className="px-4 py-3 text-slate-300">All models in your comparison made this claim. High confidence—models converge on this point.</td>
                    </tr>
                    <tr className="hover:bg-slate-800/30">
                      <td className="px-4 py-3 text-2xl">🔵</td>
                      <td className="px-4 py-3 font-semibold text-white">Majority Agreement</td>
                      <td className="px-4 py-3 text-slate-300">Most models (but not all) made this claim. Generally reliable, but worth noting which models didn't include it.</td>
                    </tr>
                    <tr className="hover:bg-slate-800/30">
                      <td className="px-4 py-3 text-2xl">🟡</td>
                      <td className="px-4 py-3 font-semibold text-white">Disputed</td>
                      <td className="px-4 py-3 text-slate-300">Some models made this claim, others didn't—or models stated conflicting information. Requires your judgment.</td>
                    </tr>
                    <tr className="hover:bg-slate-800/30">
                      <td className="px-4 py-3 text-2xl">⚪</td>
                      <td className="px-4 py-3 font-semibold text-white">Unique Insight</td>
                      <td className="px-4 py-3 text-slate-300">Only one model made this claim. Could be valuable information others missed—or an outlier to verify.</td>
                    </tr>
                  </tbody>
                </table>
              </div>

              <div className="mt-4 bg-cyan-900/20 border border-cyan-500/30 rounded-lg p-4">
                <p className="text-sm text-cyan-200">
                  <strong>Similarity scores</strong> (e.g., "89% similarity") indicate how closely the matched claims align in meaning.
                </p>
              </div>
            </section>

            {/* How Explanations Work */}
            <section>
              <h3 className="text-xl font-bold text-white mb-4 flex items-center gap-2">
                <span className="text-cyan-400">💬</span>
                How Explanations Work
              </h3>
              <div className="text-slate-300 text-sm leading-relaxed space-y-3">
                <p>
                  For any claim group, you can click <strong>"Explain why"</strong> to get an AI-generated explanation
                  of why those claims were grouped together.
                </p>

                <div className="bg-slate-800/30 rounded-lg border border-slate-700/50 p-4">
                  <p className="text-sm font-semibold text-white mb-2">What you'll see:</p>
                  <ul className="list-disc list-inside space-y-1 ml-4">
                    <li>Why the claims are considered similar or different</li>
                    <li>Key nuances between how different models stated the claim</li>
                    <li>Context for disputed or unique claims</li>
                  </ul>
                </div>

                <p className="text-xs text-slate-400 italic">
                  Note: Generating explanations uses tokens from your account.
                </p>
              </div>
            </section>

            {/* How to Read the Results */}
            <section>
              <h3 className="text-xl font-bold text-white mb-4 flex items-center gap-2">
                <span className="text-cyan-400">📊</span>
                How to Read the Results
              </h3>
              <div className="text-slate-300 text-sm leading-relaxed space-y-3">
                <div>
                  <p className="font-semibold mb-1">Summary Cards</p>
                  <p>The four cards at the top show counts for each category. Click any card to filter and see only claims of that type.</p>
                </div>

                <div>
                  <p className="font-semibold mb-1">Claim Cards</p>
                  <p>Each card shows:</p>
                  <ul className="list-disc list-inside ml-4 space-y-1">
                    <li>The representative claim text</li>
                    <li>Which models made this claim (shown as badges)</li>
                    <li>Similarity percentage</li>
                    <li><strong>Sources cited</strong> (expandable list below the claim)
                      <ul className="list-circle list-inside ml-4 mt-1 space-y-0.5 text-xs">
                        <li><strong className="text-cyan-400">Shared sources</strong> (cyan highlight) = Cited by 2+ models → Higher confidence</li>
                        <li><strong>Unique sources</strong> (gray) = Cited by 1 model only → May indicate specialized knowledge</li>
                        <li>Click "Show sources" to expand the citation list</li>
                      </ul>
                    </li>
                    <li>Option to view variations (how each model phrased it)</li>
                    <li>Option to get an AI explanation</li>
                  </ul>
                </div>

                <div>
                  <p className="font-semibold mb-1">Filtering</p>
                  <ul className="list-disc list-inside ml-4 space-y-1">
                    <li>Click a summary card to filter by agreement type</li>
                    <li>Click a model badge to see only claims involving that model</li>
                    <li>Combine filters to drill down (e.g., "Disputed claims from GPT-4")</li>
                  </ul>
                </div>
              </div>
            </section>

            {/* Interactive Example */}
            <section>
              <h3 className="text-xl font-bold text-white mb-4 flex items-center gap-2">
                <span className="text-cyan-400">🎯</span>
                Try It: Interactive Example
              </h3>
              <InteractiveClaimExample />
            </section>

            {/* Why This Is Useful */}
            <section>
              <h3 className="text-xl font-bold text-white mb-4 flex items-center gap-2">
                <span className="text-cyan-400">🧩</span>
                Why This Is Useful
              </h3>

              <div className="bg-slate-800/30 rounded-lg border border-slate-700 overflow-hidden">
                <table className="w-full text-sm">
                  <thead className="bg-slate-800/50">
                    <tr>
                      <th className="px-4 py-3 text-left text-xs font-semibold text-slate-400 uppercase">Benefit</th>
                      <th className="px-4 py-3 text-left text-xs font-semibold text-slate-400 uppercase">How It Helps</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-700">
                    <tr>
                      <td className="px-4 py-3 text-white">✓ Quick confidence check</td>
                      <td className="px-4 py-3 text-slate-300">Universal agreements are likely reliable—multiple independent models converged on the same information.</td>
                    </tr>
                    <tr>
                      <td className="px-4 py-3 text-white">✓ Spot gaps</td>
                      <td className="px-4 py-3 text-slate-300">See what one model mentioned that others missed. Decide if it's valuable or an error.</td>
                    </tr>
                    <tr>
                      <td className="px-4 py-3 text-white">✓ Identify conflicts</td>
                      <td className="px-4 py-3 text-slate-300">Disputed claims highlight where you need to verify or make a judgment call.</td>
                    </tr>
                    <tr>
                      <td className="px-4 py-3 text-white">✓ Save time</td>
                      <td className="px-4 py-3 text-slate-300">Instead of reading 6 full responses, scan the grouped claims to understand the landscape.</td>
                    </tr>
                    <tr>
                      <td className="px-4 py-3 text-white">✓ Reduce noise</td>
                      <td className="px-4 py-3 text-slate-300">Introductions, transitions, and filler are filtered out—you see only substance.</td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </section>

            {/* FAQ */}
            <section>
              <h3 className="text-xl font-bold text-white mb-4 flex items-center gap-2">
                <span className="text-cyan-400">❓</span>
                Frequently Asked Questions
              </h3>

              <div className="space-y-4">
                <div className="bg-slate-800/30 rounded-lg p-4 border border-slate-700/50">
                  <h4 className="font-semibold text-white mb-2">How is this different from the Scoring feature?</h4>
                  <p className="text-sm text-slate-400">
                    Scoring evaluates response <em>quality</em> (accuracy, clarity, completeness). Consensus Analysis
                    compares response <em>content</em> (what claims each model made and where they agree or differ).
                  </p>
                </div>

                <div className="bg-slate-800/30 rounded-lg p-4 border border-slate-700/50">
                  <h4 className="font-semibold text-white mb-2">Why might a claim show as "Disputed"?</h4>
                  <p className="text-sm text-slate-400">
                    Either different models stated conflicting information, or some models included a claim that others omitted entirely.
                    Check the variations to see exactly what each model said.
                  </p>
                </div>

                <div className="bg-slate-800/30 rounded-lg p-4 border border-slate-700/50">
                  <h4 className="font-semibold text-white mb-2">What does the similarity percentage mean?</h4>
                  <p className="text-sm text-slate-400">
                    It measures how closely matched claims align in meaning. 95%+ means nearly identical claims with minor wording differences.
                    70-85% means related claims with some variation in detail or framing.
                  </p>
                </div>

                <div className="bg-slate-800/30 rounded-lg p-4 border border-slate-700/50">
                  <h4 className="font-semibold text-white mb-2">Can I trust Universal Agreements?</h4>
                  <p className="text-sm text-slate-400">
                    Universal agreement means all models converged on the same claim—but it doesn't guarantee correctness.
                    Models can share the same training biases. Use it as a confidence signal, not proof.
                  </p>
                </div>

                <div className="bg-slate-800/30 rounded-lg p-4 border border-slate-700/50">
                  <h4 className="font-semibold text-white mb-2">Does running Consensus Analysis cost tokens?</h4>
                  <p className="text-sm text-slate-400">
                    Yes—extracting claims and generating embeddings uses tokens from your account. The cost is shown before you run the analysis.
                    Cached results (re-viewing the same analysis) are free.
                  </p>
                </div>

                <div className="bg-slate-800/30 rounded-lg p-4 border border-slate-700/50">
                  <h4 className="font-semibold text-white mb-2">How do citations work?</h4>
                  <p className="text-sm text-slate-400">
                    Citations (URLs, references, and DOIs) are automatically extracted from model responses during Consensus Analysis.{' '}
                    <strong className="text-cyan-400">Shared sources</strong> (highlighted in cyan) indicate that multiple models referenced
                    the same source, which can signal agreement on authoritative material and increase confidence in the claim.{' '}
                    <strong>Unique sources</strong> appear when only one model cited a particular reference—these may represent specialized
                    knowledge or sources that other models missed.
                  </p>
                </div>
              </div>
            </section>

            {/* Important Disclaimer */}
            <section>
              <div className="bg-yellow-900/20 border border-yellow-500/30 rounded-lg p-4">
                <h3 className="text-lg font-bold text-yellow-200 mb-2 flex items-center gap-2">
                  <span>⚠️</span>
                  Important Disclaimer
                </h3>
                <div className="text-yellow-200/80 text-sm space-y-2">
                  <p className="font-semibold">Consensus Analysis is AI-generated and may contain errors.</p>
                  <ul className="list-disc list-inside ml-4 space-y-1">
                    <li>Claim extraction, grouping, and explanations are produced by AI systems</li>
                    <li>The analysis may miss nuances, misgroup claims, or misinterpret content</li>
                    <li>Universal agreement across models does not guarantee factual accuracy—models can share the same errors or biases</li>
                    <li>Always verify important information from authoritative sources before making decisions</li>
                  </ul>
                  <p className="mt-3">
                    Use Consensus Analysis as a <strong>tool to assist your evaluation</strong>, not as a definitive source of truth.
                  </p>
                </div>
              </div>
            </section>
          </div>
        </div>
      </div>
    </div>,
    document.body
  );
};
